import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, EventEmitter,
  Input,
  OnChanges, Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ResultEntrySearchFilter} from "../../models/SearchFilter";
import {BehaviorSubject, Subject} from "rxjs";
import DataSource from "devextreme/data/data_source";
import {OrderService} from "../../services/order.service";
import {DxDataGridComponent} from "devextreme-angular";
import {OrderStatus} from "../../../results/models/order";
import {Order} from "../../models/order";
import {DomSanitizer} from "@angular/platform-browser";
import {FusionAuthService} from "../../../../../shared/services/fusion-auth";
import {ClientService} from "../../../../shared/services";
import {OptionChangedEvent, SelectionChangedEvent} from "devextreme/ui/data_grid";
import {take, takeUntil} from "rxjs/operators";
import {FileUtils} from "../../../../../shared/utils";
import {ReportsService} from "../../../../shared/services/reports.service";
import {PWADetectorService} from "../../../../../shared/services/general";

@Component({
  selector: 'app-orders-grid',
  templateUrl: './orders-grid.component.html',
  styleUrls: ['./orders-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrdersGridComponent implements OnChanges {
  @Input() public filters?: ResultEntrySearchFilter;
  @Input() public clean?: boolean;
  @Output() public selectionChanged: EventEmitter<Order[]> = new EventEmitter<Order[]>();
  @Output() public previewClicked: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild(DxDataGridComponent, {static: true}) public dxDataGridComponent?: DxDataGridComponent;
  public printDialogVisible = false;
  public excludedOrders: number[] = [];
  public statusHeaderFilter = [
    {
      text: 'PENDING',
      value: ['status', '=', OrderStatus.Pending],
    },
    {
      text: 'UNRELEASED',
      value: ['status', '=', OrderStatus.Unreleased],
    },
    {
      text: 'PARTIAL',
      value: ['status', '=', OrderStatus.Partial],
    },
    {
      text: 'PARTIALLY RELEASED',
      value: ['status', '=', OrderStatus.PartiallyReleased],
    },
    {
      text: 'FINAL',
      value: ['status', '=', OrderStatus.Final],
    },
  ];
  public selectedOrderIds: number[] = [];
  #selectedOrders: Order[] = [];
  public dataSource: BehaviorSubject<DataSource | null> = new BehaviorSubject<DataSource | null>(null);
  public onDestroy$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  constructor(
    private orderService: OrderService,
    private clientService: ClientService,
    private fusionAuthService: FusionAuthService,
    private sanitizer: DomSanitizer,
    private pwaDetectorService: PWADetectorService,
    public changeDetector: ChangeDetectorRef,
    private reportsService: ReportsService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleFiltersChange(changes);
    if (changes.clean && changes.clean.currentValue) {
      this.dxDataGridComponent?.instance.clearSelection();
      this.dxDataGridComponent?.instance.beginCustomLoading("Loading...");
      this.dxDataGridComponent?.instance.getDataSource().reload();
      this.dxDataGridComponent?.instance.refresh();
    }
  }

  public optionChanged(event: OptionChangedEvent) {
    if (event.name === "paging") {
      this.dxDataGridComponent?.instance.clearSelection();
      this.selectedOrderIds = [];
      this.#selectedOrders = [];
      this.selectionChanged.emit(this.#selectedOrders);
    }
  }

  public handleSelectionChange() {
    this.#selectedOrders = this.dxDataGridComponent?.instance.getSelectedRowsData() as Order[];
    this.selectionChanged.emit(this.#selectedOrders);
  }

  private handleFiltersChange(changes: SimpleChanges) {
    if (changes.filters && changes.filters.currentValue) {
      this.excludedOrders = [];
      this.dataSource.next(new DataSource({
        store: this.orderService.searchStore(changes.filters.currentValue, this.dataSource)
      }));

      this.selectedOrderIds = [];
      this.selectionChanged.next([]);

      if (this.dxDataGridComponent?.instance) {
        this.dxDataGridComponent.instance.beginCustomLoading("Loading...");
        this.dataSource.value?.on("changed", () => this.dxDataGridComponent?.instance.endCustomLoading());
      }
    }
  }

  public get DataSource() {
    return this.dataSource.value ? this.dataSource.value : [];
  }

  public showColumnChooser() {
    this.dxDataGridComponent?.instance.showColumnChooser();
  }

  public getStatus(order: Order) {
    let color;
    let status: string = OrderStatus[OrderStatus.None];
    switch (order.status) {
      case OrderStatus.Canceled:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.Canceled];
        break;
      case OrderStatus.Final:
        color = 'ocl-status-green';
        status = OrderStatus[OrderStatus.Final];
        break;
      case OrderStatus.Partial:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Partial];
        break;
      case OrderStatus.PartiallyReleased:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.PartiallyReleased];
        break;
      case OrderStatus.Preliminary:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Preliminary];
        break;
      case OrderStatus.Pending:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Pending];
        break;
      case OrderStatus.Unreleased:
        color = 'ocl-status-yellow';
        status = OrderStatus[OrderStatus.Unreleased];
        break;
      case OrderStatus.None:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.None];
        break;
    }

    let container = `
            <div fxLayout='column' fxLayoutAlign='center'>
                <div style='width: 100px;height:15px!important;line-height: .45 !important' class='${color}'>
                ${status.toUpperCase()}</div>
        `;
    container += `</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(container);
  }

  public excludeOrders() {
    const idsToExclude = this.selectedOrderIds;
    if (this.excludedOrders.length > 0)
      idsToExclude.push(...this.excludedOrders);

    this.#selectedOrders = [];
    this.selectedOrderIds = [];
    this.dxDataGridComponent?.instance.beginCustomLoading("Loading...");
    const filters: any[] = [];
    for (let i = 0; i < idsToExclude.length; i++) {
      filters.push(["!", ["id", "=", idsToExclude[i]]])
      if (i < idsToExclude.length -1 ) {
        filters.push("and")
      }
      this.excludedOrders.push(idsToExclude[i]);
    }
    this.dxDataGridComponent?.instance.getDataSource().filter(filters);
    this.dxDataGridComponent?.instance.getDataSource().reload();
  }

  public printLabels() {
    this.printDialogVisible = true;
  }

  public printOrderDetails() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderDetails(this.#selectedOrders.map(o => o.id))
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public cleanExcluded() {
    this.excludedOrders = [];
    this.selectedOrderIds = [];
    this.excludeOrders();
  }
}
